// @ts-check

import React, { ReactElement, ReactNode, PropsWithChildren, Children, useState, useEffect } from 'react'
import { FieldErrors, UseFormRegister } from 'react-hook-form'

type ValidationType = {
  [key:string]: any
}

type ValidationMessagesType = {
  [key:string]: string
}

export interface FormGroupItemProps {
  name?: string
  id: string
  defaultChecked?: boolean
  label?: string
  className?: string
  disabled?: boolean
  errors?: FieldErrors
  register: UseFormRegister<any>
  validations?: ValidationType
}

const Component = (props: {
  id: string
  label?: string
  className?: string
  errors?: FieldErrors
  children?: ReactNode
}): ReactElement => {
  const {
    id,
    label,
    className,
    errors,
    children
  } = props

  const [hasErrors, setHasErrors] = useState<boolean>(false)
  const [isRequired, setIsRequired] = useState<boolean>(false)
  const [childrenNames, setChildrenNames] = useState<Array<string>>([])
  const [childrenValidations, setChildrenValidations] = useState<ValidationType>({})
  const [validationMessages, setValidationMessages] = useState<ValidationMessagesType>({})

  useEffect(() => {
    if (children) {
      const childrenArray = Children.toArray(children)
      const childrenNamesArr: Array<string> = []
      const childrenValidationsObj: ValidationType = {}

      Children.map(childrenArray, (child) => {
        const item = child as ReactElement<PropsWithChildren<FormGroupItemProps>>

        if (item.props.name) {
          childrenNamesArr.push(item.props.name)
        } else {
          childrenNamesArr.push(item.props.id)
        }

        if (item.props.validations) {
          Object.assign(childrenValidationsObj, item.props.validations)
        }
      })

      setChildrenNames(childrenNamesArr)
      setChildrenValidations(childrenValidationsObj)
    }
  }, [children])

  useEffect(() => {
    let groupHasErrors = false
    if (errors && childrenNames) {
      Object.keys(errors).map((error: any) => {
        if (childrenNames.includes(error)) {
          groupHasErrors = true
        }
        return false
      })
    }
    setHasErrors(groupHasErrors)
  }, [errors, childrenNames])

  useEffect(() => {
    // Construct validation variables from validations object
    const validationMessagesObj: ValidationMessagesType = {}
    let requiredChildren = false
    if (childrenValidations) {
      Object.keys(childrenValidations).map((key) => {
        validationMessagesObj[key] = key === 'required' ? childrenValidations[key] : childrenValidations[key].message
        return false
      })
      requiredChildren = Object.keys(childrenValidations).filter(key => key === 'required').length > 0

      setValidationMessages(validationMessagesObj)
      setIsRequired(requiredChildren)
    }
  }, [childrenValidations])

  const classes = [
    'form-item',
    'form-group-item',
    className,
    errors && hasErrors
      ? 'error'
      : '',
    label
      ? 'form-group-item--with-label'
      : ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes}>
      {label && (
        <label
          htmlFor={id}
          className={isRequired ? 'form-required' : ''}
        >
          {label}
        </label>
      )}
      <div>
        {children}
      </div>
      {errors && hasErrors && validationMessages &&
        <div className="mt-2 text-sm form-item--error-message">
          {Object.values(validationMessages).map((msg) => (<div>{msg}</div>))}
        </div>
      }
    </div>
  )
}

export default Component
